import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import OrdenListItem from './Ordenes/OrdenListItem';
import PublicacionListItem from './Publicaciones/Components/PublicacionListItem';
import BigSpinner from './General/BigSpinner';
import GoBackArrow from './General/GoBackArrow';
import { resetPublicaciones } from '../actions/publicacionesActions';
import { resetBusquedaGlobalResultados } from '../actions/busquedaGlobalActions';
import { getTipoEntregaString } from '../helper/publicacionHelper';

function BusquedaGlobalResultados() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const idSeller = useSelector(state => state.general.idSeller);
    const userType = useSelector(state => state.general.userType) || {};
    const isAdminMod = userType.isAdminMod ?? false;
    const resultadosStore = useSelector(state => state.busquedaGlobal.resultados);

    // Se espera que resultados tenga { publicaciones: [], ordenes: [] }
    const { resultados, loadingGlobal } = location.state || {};
    // Si no hay resultados en location.state, usar los del store
    const resultadosFinal = resultados || resultadosStore;

    // Si está cargando o backend indicó error, mostramos spinner; si hay error mostramos mensaje
    if (loadingGlobal || (resultadosFinal && resultadosFinal.tieneError)) {
        return (
            <div className="container mt-4" style={{ minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <BigSpinner />
                {resultadosFinal && resultadosFinal.tieneError && (
                    <div className="mt-3 alert alert-danger w-100 text-center">SE PRODUJO UN ERROR</div>
                )}
            </div>
        );
    }

    const { publicaciones = [], ordenes = [] } = resultadosFinal || {};

    const handleEstado = (event, args) => {
        if (event && typeof event.stopPropagation === 'function') {
            event.stopPropagation();
        }
    };

    const handleOnOrdenes = (event, skuMarket) => {
        if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
        navigate('/Ordenes/Publicacion', {
            state: {
                pathReturn: '/BusquedaGlobalResultados',
                skuMarket: skuMarket
            }
        });
    };

    const handlePublicacionClick = (publicacion) => {
        dispatch(resetPublicaciones());
        navigate(`/Publicaciones/Editar/${publicacion.idSeller}/${publicacion.idPublicacion}`, {
            state: {
                pathReturn: '/BusquedaGlobalResultados',
                idSellerPub: publicacion.idSeller,
                tieneRebate: publicacion.tieneRebate
            }
        });
    };

    const handleOrdenClick = (orden) => {
        navigate(`/Ordenes/Editar/${orden.idOrden}`, {
            state: '/BusquedaGlobalResultados'
        });
    };

    return (
        <div className="container mt-4">
            <div className="d-flex align-items-center mb-5">
                <div className="me-2">
                    <GoBackArrow onBack={() => {
                        // Clear search input UI and redux state, then navigate home
                        try { window.dispatchEvent(new Event('clearSearchInput')); } catch(e){}
                        try { dispatch(resetBusquedaGlobalResultados()); } catch(e){}
                        navigate('/');
                    }} />
                </div>
                <h3 className="text-themecolor color-rojo-megatone mb-0">Resultados de la búsqueda global</h3>
            </div>

            <h4 className="text-themecolor color-rojo-megatone">Publicaciones</h4>
            {publicaciones.length > 0 ? (
                <div className="list-group mb-4">
                    {publicaciones.map((publicacion, i) => (
                        <div
                            key={i}
                            className="list-group-item p-0"
                            onMouseEnter={(e) => handleEstado(e, { idPublicacion: publicacion.idPublicacion })}
                            onClick={() => handlePublicacionClick(publicacion)}
                            style={{ cursor: 'pointer', textDecoration: 'none' }}
                        >
                            <PublicacionListItem
                                titulo={publicacion.titulo}
                                skuSeller={publicacion.skuSeller}
                                skuMarket={publicacion.skuMarket}
                                categoria={publicacion.categoria}
                                precioLista={publicacion.precioLista}
                                precioPromocional={publicacion.precioPromocional}
                                estado={publicacion.estado}
                                img={publicacion.imagenes && publicacion.imagenes.length > 0 ? publicacion.imagenes[0].urlImagen : ''}
                                idPublicacion={publicacion.idPublicacion}
                                marca={publicacion.marca}
                                tipoEntrega={
                                    Array.isArray(publicacion.tipoEntrega)
                                        ? getTipoEntregaString(publicacion.tipoEntrega)
                                        : (typeof publicacion.tipoEntrega === 'string' ? publicacion.tipoEntrega : '')
                                }
                                fechaPublicacion={publicacion.fechaPublicacion}
                                envioGratis={publicacion.envioGratis}
                                handleEstado={handleEstado}
                                descripcionSeller={publicacion.descripcionSeller}
                                tipoPublicacion={publicacion.tipoPublicacion}
                                stock={publicacion.stock}
                                isAdminOrCategory={true}
                                productoExceptuado={publicacion.exceptuado}
                                hideCheckboxEstado={true}
                                hideBtnOrdenes={true}
                            />
                        </div>
                    ))}
                </div>
            ) : (
                <h5 className="d-flex justify-content-center my-4">NO SE ENCONTRARON PUBLICACIONES</h5>
            )}

            <h4 className="text-themecolor color-rojo-megatone">Órdenes</h4>
            {ordenes.length > 0 ? (
                <div className="list-group mb-4">
                    {ordenes.map((orden, i) => {
                        const sellerCodigo = orden?.seller?.codigo;

                        // Determine tipoEntrega text from multiple possible shapes returned by API
                        const tipoEntregaText = (
                            orden?.entrega?.tipoEntrega?.descripcion ||
                            (orden?.tipoEntrega && typeof orden.tipoEntrega === 'object' ? orden.tipoEntrega.descripcion : null) ||
                            orden?.tipoEntrega ||
                            orden?.entrega?.tipoEntrega ||
                            ''
                        );

                        return (
                            <div
                                key={i}
                                className="list-group-item p-0"
                                onClick={() => handleOrdenClick(orden)}
                                style={{ cursor: 'pointer' }}
                            >
                                <OrdenListItem
                                    idOrden={orden.idOrden}
                                    idReferencia={orden.idReferencia}
                                    seller={orden.seller?.descripcion}
                                    fecha={orden.fecha}
                                    cliente={orden.cliente?.nombre + ' ' + orden.cliente?.apellido}
                                    mailCliente={orden.cliente?.email}
                                    documento={orden.cliente?.documento}
                                    tipoEntrega={tipoEntregaText}
                                    montoVenta={orden.montoVenta}
                                    productos={orden.productos}
                                    estado={orden.estado}
                                    cuotas={orden.pago?.cuotas}
                                    tarjeta={orden.pago?.tarjeta}
                                    urlLogo={orden.seller?.urlLogo}
                                    dniIdBocaSeller={orden.idVendedorBoca}
                                    plataformaCodigo={orden.plataforma?.codigo}
                                    importeComision={orden.montoComision}
                                    anulacionMegatone={orden.anulacionMegatone}
                                    showComision={idSeller === sellerCodigo && !isAdminMod ? false : true}
                                />
                            </div>
                        );
                    })}
                </div>
            ) : (
                <h5 className="d-flex justify-content-center my-4">NO SE ENCONTRARON ÓRDENES</h5>
            )}
        </div>
    );
}

export default BusquedaGlobalResultados;
